/**
 * DocumentReference schemas — file uploads, addendums, practice legal docs.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type {
  DocumentReferenceStatus as FhirDocStatus,
  DocumentTypeCode as FhirDocTypeCode,
  DocumentCategoryCode as FhirDocCategoryCode,
  AddendumReasonForChange as FhirAddendumReason,
} from '@unify/fhir-model/resources/document-reference';

/** FHIR DocumentReference.status values — synced with fhir-model. */
export const documentStatusValues = [
  'current',
  'superseded',
  'entered-in-error',
] as const satisfies readonly FhirDocStatus[];

/** Unify document type codes — synced with fhir-model. */
export const documentTypeValues = [
  'admin-note',
  'discharge-summary',
  'clinical-document',
  'progress-note-addendum',
  'document',
  'fax',
] as const satisfies readonly FhirDocTypeCode[];

/** Unify document category codes — synced with fhir-model. */
export const documentCategoryValues = [
  'admin',
  'clinical',
  'addendum',
] as const satisfies readonly FhirDocCategoryCode[];

/**
 * Client document upload — FormsFilesTab file upload path.
 */
export const uploadDocumentSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  fileName: z.string().min(1, 'File name is required'),
  contentType: z.string().min(1, 'Content type is required'),
  documentType: z.enum(documentTypeValues).optional(),
  documentCategory: z.enum(documentCategoryValues).optional(),
  description: z.string().optional(),
  accountId: z.string().optional(),
});

export type UploadDocumentInput = z.infer<typeof uploadDocumentSchema>;

/** Addendum reason-for-change codes — synced with fhir-model. */
export const addendumReasonValues = [
  'typo',
  'clarification',
  'other',
] as const satisfies readonly FhirAddendumReason[];

/**
 * Addendum creation — links to a parent QuestionnaireResponse.
 */
export const createAddendumSchema = z.object({
  parentQuestionnaireResponseId: z.string().min(1, 'Parent note is required'),
  patientId: z.string().min(1, 'Patient is required'),
  authorReference: z.string().min(1, 'Author is required'),
  reasonForChange: z.enum(addendumReasonValues),
  content: z.string().min(1, 'Addendum content is required'),
  encounterId: z.string().optional(),
  appointmentId: z.string().optional(),
});

export type CreateAddendumInput = z.infer<typeof createAddendumSchema>;
